/**
 * 分类**身份层** —— 业务唯一源（架构 §4.6 · `DR-021` ⑧）
 *
 * 本文件**只承载业务身份**：`id` / `name` / `kind` / 层级 / `parentId` / `system`。
 * **呈现部分（图标绑定、色板、字形池）在设计资产**，两者由 `tools/gen-categories.ts`
 * **构建期合并** ⇒ 设计改配色/图标**不必走代码发版**（这正是分工的目的）。
 *
 * ## 三条铁律（架构 §4.6）
 *
 * 1. 🔴 **`id` 一经分配即不可变** —— 改名不改 `id`（补 `id` 的唯一目的就是让改名不断引用）；
 * 2. 🔴 **禁按中文名引用分类** —— 代码里只允许写 `CAT.*` 常量；执行手段 = 架构规则
 *    `no-cat-name-literal`（`DR-023` D4）；
 * 3. 🔴 **`system=true` 的数量与 `id` 集合由 CI 断言守住**（`gen-categories.ts --check`）。
 *
 * ## 维护方式
 *
 * - **新增 / 改名 / 调序**：直接改本文件 → `pnpm gen:categories -- --write` 重生成产物；
 * - **图标绑定**：改设计资产（本文件不含图标）；
 * - ⚠️ **不要**为了新增分类而手改 `generated/categories.generated.ts` —— 它是产物。
 *
 * ✅ **当前范围（2026-10-13 `O2-C1` 已收口）**：支出 **18 L1 / 107 L2** + 收入 **9 L1 / 26 L2**
 * = **27 L1 / 133 L2 / 合计 160**（规格 §2 定稿口径；`DR-041`）。
 * 生成器 `gen-categories --check` 的 ① 双向无孤儿 / ② `system` 一致 / ③ 层级自洽 / ④ `CAT` 不碰撞 / ⑤ 产物最新，
 * 五条断言即守此表；基数哨兵在 `categories.spec.ts`。
 * ⚠️ 规格 §2 条文里 2026-09-27 登记的「103 L2」是**推导值、与追加清单不自洽**；
 * 已按 `铁律 23`（自指数字必须实测）订正为 **107**，见规格 §24 之三十二。
 */

import type { CategoryKind } from './platform.js';

/** 分类身份。`level` 1 = L1（大类）· 2 = L2（子类）。 */
export interface CategoryIdentity {
  readonly id: string;
  readonly name: string;
  readonly kind: CategoryKind;
  /** 1 = L1 · 2 = L2（L2 的 `kind` 恒等于父级） */
  readonly level: 1 | 2;
  /** L1 为 `null`；L2 为其 L1 的 `id` */
  readonly parentId: string | null;
  /** `true` = 系统分类（禁改名禁删除，`id` 与标记由 CI 锁定，规格 §22.4） */
  readonly system: boolean;
}

/**
 * 全量分类身份。
 *
 * 🔴 **数组顺序 = 展示顺序**（L1 之间按本数组序；L2 在同父内按本数组序）——
 * 架构 §4.6 身份层含「排序」，此处以**数组位置**表达，seed 时按索引写 `sort`，
 * 因此**调序必须改本文件**（而不是在别处写排序字段）。
 */
export const CATEGORIES: readonly CategoryIdentity[] = [
  { id: 'cat-meal', name: '餐饮', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-meal-breakfast', name: '早餐', kind: 'expense', level: 2, parentId: 'cat-meal', system: false },
  { id: 'cat-meal-lunch', name: '午餐', kind: 'expense', level: 2, parentId: 'cat-meal', system: false },
  { id: 'cat-meal-dinner', name: '晚餐', kind: 'expense', level: 2, parentId: 'cat-meal', system: false },
  { id: 'cat-meal-latenight', name: '夜宵', kind: 'expense', level: 2, parentId: 'cat-meal', system: false },
  { id: 'cat-meal-takeout', name: '外卖', kind: 'expense', level: 2, parentId: 'cat-meal', system: false },
  { id: 'cat-meal-fastfood', name: '快餐小吃', kind: 'expense', level: 2, parentId: 'cat-meal', system: false },
  { id: 'cat-meal-coffee', name: '咖啡茶饮', kind: 'expense', level: 2, parentId: 'cat-meal', system: false },
  { id: 'cat-meal-snack', name: '零食甜品', kind: 'expense', level: 2, parentId: 'cat-meal', system: false },
  { id: 'cat-meal-dining', name: '聚餐请客', kind: 'expense', level: 2, parentId: 'cat-meal', system: false },
  { id: 'cat-meal-tobacco-alcohol', name: '烟酒茶', kind: 'expense', level: 2, parentId: 'cat-meal', system: false },
  { id: 'cat-grocery', name: '食品生鲜', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-grocery-veggie-fruit', name: '蔬菜水果', kind: 'expense', level: 2, parentId: 'cat-grocery', system: false },
  { id: 'cat-grocery-meat-dairy', name: '肉禽蛋奶', kind: 'expense', level: 2, parentId: 'cat-grocery', system: false },
  { id: 'cat-grocery-seafood', name: '海鲜水产', kind: 'expense', level: 2, parentId: 'cat-grocery', system: false },
  { id: 'cat-grocery-staple-seasoning', name: '粮油调味', kind: 'expense', level: 2, parentId: 'cat-grocery', system: false },
  { id: 'cat-grocery-bakery', name: '烘焙面点', kind: 'expense', level: 2, parentId: 'cat-grocery', system: false },
  { id: 'cat-grocery-fresh-delivery', name: '生鲜配送', kind: 'expense', level: 2, parentId: 'cat-grocery', system: false },
  { id: 'cat-grocery-convenience', name: '便利店', kind: 'expense', level: 2, parentId: 'cat-grocery', system: false },
  { id: 'cat-transit', name: '交通', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-transit-bus-metro', name: '公交地铁', kind: 'expense', level: 2, parentId: 'cat-transit', system: false },
  { id: 'cat-transit-taxi', name: '打车网约', kind: 'expense', level: 2, parentId: 'cat-transit', system: false },
  { id: 'cat-transit-bike', name: '共享单车', kind: 'expense', level: 2, parentId: 'cat-transit', system: false },
  { id: 'cat-transit-fuel', name: '加油充电', kind: 'expense', level: 2, parentId: 'cat-transit', system: false },
  { id: 'cat-transit-parking-toll', name: '停车过路', kind: 'expense', level: 2, parentId: 'cat-transit', system: false },
  { id: 'cat-transit-flight-train', name: '机票火车', kind: 'expense', level: 2, parentId: 'cat-transit', system: false },
  { id: 'cat-transit-car-maintenance', name: '车辆保养', kind: 'expense', level: 2, parentId: 'cat-transit', system: false },
  { id: 'cat-transit-car-rental', name: '租车', kind: 'expense', level: 2, parentId: 'cat-transit', system: false },
  { id: 'cat-shopping', name: '购物', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-shopping-daily', name: '日用百货', kind: 'expense', level: 2, parentId: 'cat-shopping', system: false },
  { id: 'cat-shopping-home-textile', name: '家居家纺', kind: 'expense', level: 2, parentId: 'cat-shopping', system: false },
  { id: 'cat-shopping-kitchenware', name: '厨具餐具', kind: 'expense', level: 2, parentId: 'cat-shopping', system: false },
  { id: 'cat-shopping-book', name: '书籍文具', kind: 'expense', level: 2, parentId: 'cat-shopping', system: false },
  { id: 'cat-shopping-secondhand', name: '二手闲置', kind: 'expense', level: 2, parentId: 'cat-shopping', system: false },
  { id: 'cat-shopping-online-other', name: '网购其他', kind: 'expense', level: 2, parentId: 'cat-shopping', system: false },
  // 🔴 2026-10-13 `O2-C1` 补齐（规格 §2「数码家电 / 服饰美容 并入 购物 · L2 一个未减」）——
  //    这 6 条是「服饰美容」L1 收敛后下沉的 L2，v1 目标账本逐条有对应，缺失会让迁移丢分类。
  { id: 'cat-shopping-clothing', name: '衣服鞋帽', kind: 'expense', level: 2, parentId: 'cat-shopping', system: false },
  { id: 'cat-shopping-apparel', name: '衣服', kind: 'expense', level: 2, parentId: 'cat-shopping', system: false },
  { id: 'cat-shopping-bags-shoes', name: '鞋包', kind: 'expense', level: 2, parentId: 'cat-shopping', system: false },
  { id: 'cat-shopping-accessories', name: '配饰', kind: 'expense', level: 2, parentId: 'cat-shopping', system: false },
  { id: 'cat-shopping-hair-beauty', name: '美发美容', kind: 'expense', level: 2, parentId: 'cat-shopping', system: false },
  { id: 'cat-shopping-skincare', name: '护肤化妆', kind: 'expense', level: 2, parentId: 'cat-shopping', system: false },
  { id: 'cat-shopping-digital', name: '数码电器', kind: 'expense', level: 2, parentId: 'cat-shopping', system: false },
  { id: 'cat-housing', name: '住房', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-housing-rent', name: '房租', kind: 'expense', level: 2, parentId: 'cat-housing', system: false },
  { id: 'cat-housing-property-fee', name: '物业费', kind: 'expense', level: 2, parentId: 'cat-housing', system: false },
  { id: 'cat-housing-repair', name: '房屋维修', kind: 'expense', level: 2, parentId: 'cat-housing', system: false },
  { id: 'cat-housing-renovation', name: '装修软装', kind: 'expense', level: 2, parentId: 'cat-housing', system: false },
  { id: 'cat-housing-cleaning', name: '家居清洁', kind: 'expense', level: 2, parentId: 'cat-housing', system: false },
  { id: 'cat-utility', name: '生活缴费', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-utility-water', name: '水费', kind: 'expense', level: 2, parentId: 'cat-utility', system: false },
  { id: 'cat-utility-electricity', name: '电费', kind: 'expense', level: 2, parentId: 'cat-utility', system: false },
  { id: 'cat-utility-gas', name: '燃气费', kind: 'expense', level: 2, parentId: 'cat-utility', system: false },
  { id: 'cat-utility-heating', name: '供暖费', kind: 'expense', level: 2, parentId: 'cat-utility', system: false },
  { id: 'cat-utility-waste', name: '垃圾处理', kind: 'expense', level: 2, parentId: 'cat-utility', system: false },
  // 🔴 2026-10-13 `O2-C1` 补齐（规格 §2「通讯 并入 生活缴费 · L2 一个未减」）
  { id: 'cat-utility-phone', name: '话费', kind: 'expense', level: 2, parentId: 'cat-utility', system: false },
  { id: 'cat-utility-broadband', name: '宽带网络', kind: 'expense', level: 2, parentId: 'cat-utility', system: false },
  { id: 'cat-utility-express', name: '快递', kind: 'expense', level: 2, parentId: 'cat-utility', system: false },
  { id: 'cat-medical', name: '医疗健康', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-medical-clinic', name: '门诊挂号', kind: 'expense', level: 2, parentId: 'cat-medical', system: false },
  { id: 'cat-medical-pharmacy', name: '药品', kind: 'expense', level: 2, parentId: 'cat-medical', system: false },
  { id: 'cat-medical-checkup-vaccine', name: '体检疫苗', kind: 'expense', level: 2, parentId: 'cat-medical', system: false },
  { id: 'cat-medical-dental-eye', name: '口腔眼科', kind: 'expense', level: 2, parentId: 'cat-medical', system: false },
  { id: 'cat-medical-inpatient', name: '住院治疗', kind: 'expense', level: 2, parentId: 'cat-medical', system: false },
  { id: 'cat-medical-supplement', name: '保健营养', kind: 'expense', level: 2, parentId: 'cat-medical', system: false },
  { id: 'cat-medical-therapy', name: '心理咨询', kind: 'expense', level: 2, parentId: 'cat-medical', system: false },
  { id: 'cat-education', name: '教育', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-education-tuition', name: '学费', kind: 'expense', level: 2, parentId: 'cat-education', system: false },
  { id: 'cat-education-tutoring', name: '课外辅导', kind: 'expense', level: 2, parentId: 'cat-education', system: false },
  { id: 'cat-education-hobby-class', name: '兴趣班', kind: 'expense', level: 2, parentId: 'cat-education', system: false },
  { id: 'cat-education-course', name: '书籍课程', kind: 'expense', level: 2, parentId: 'cat-education', system: false },
  { id: 'cat-education-vocational', name: '职业培训', kind: 'expense', level: 2, parentId: 'cat-education', system: false },
  { id: 'cat-education-exam-cert', name: '考试认证', kind: 'expense', level: 2, parentId: 'cat-education', system: false },
  { id: 'cat-social', name: '人情社交', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-social-redpacket', name: '红包礼金', kind: 'expense', level: 2, parentId: 'cat-social', system: false },
  { id: 'cat-social-gift-money', name: '份子钱', kind: 'expense', level: 2, parentId: 'cat-social', system: false },
  { id: 'cat-social-gift', name: '礼物', kind: 'expense', level: 2, parentId: 'cat-social', system: false },
  { id: 'cat-social-elder-care', name: '孝亲', kind: 'expense', level: 2, parentId: 'cat-social', system: false },
  { id: 'cat-social-gathering', name: '朋友聚会', kind: 'expense', level: 2, parentId: 'cat-social', system: false },
  { id: 'cat-social-charity', name: '慈善捐赠', kind: 'expense', level: 2, parentId: 'cat-social', system: false },
  { id: 'cat-fun', name: '娱乐休闲', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-fun-movie', name: '电影演出', kind: 'expense', level: 2, parentId: 'cat-fun', system: false },
  { id: 'cat-fun-subscription', name: '视频音乐会员', kind: 'expense', level: 2, parentId: 'cat-fun', system: false },
  { id: 'cat-fun-game', name: '游戏', kind: 'expense', level: 2, parentId: 'cat-fun', system: false },
  { id: 'cat-fun-fitness', name: '运动健身', kind: 'expense', level: 2, parentId: 'cat-fun', system: false },
  { id: 'cat-fun-sightseeing', name: '景点游玩', kind: 'expense', level: 2, parentId: 'cat-fun', system: false },
  { id: 'cat-fun-travel', name: '度假旅行', kind: 'expense', level: 2, parentId: 'cat-fun', system: false },
  { id: 'cat-fun-boardgame', name: '桌游棋牌', kind: 'expense', level: 2, parentId: 'cat-fun', system: false },
  { id: 'cat-baby', name: '母婴亲子', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-baby-formula-diaper', name: '奶粉尿裤', kind: 'expense', level: 2, parentId: 'cat-baby', system: false },
  { id: 'cat-baby-toy-book', name: '玩具绘本', kind: 'expense', level: 2, parentId: 'cat-baby', system: false },
  { id: 'cat-baby-preschool', name: '早教托育', kind: 'expense', level: 2, parentId: 'cat-baby', system: false },
  { id: 'cat-baby-kid-clothing', name: '儿童衣物', kind: 'expense', level: 2, parentId: 'cat-baby', system: false },
  { id: 'cat-baby-kid-play', name: '儿童游乐', kind: 'expense', level: 2, parentId: 'cat-baby', system: false },
  { id: 'cat-pet', name: '宠物', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-pet-food', name: '宠物粮', kind: 'expense', level: 2, parentId: 'cat-pet', system: false },
  { id: 'cat-pet-supplies', name: '宠物用品', kind: 'expense', level: 2, parentId: 'cat-pet', system: false },
  { id: 'cat-pet-vet', name: '宠物医疗', kind: 'expense', level: 2, parentId: 'cat-pet', system: false },
  { id: 'cat-pet-grooming', name: '洗护美容', kind: 'expense', level: 2, parentId: 'cat-pet', system: false },
  { id: 'cat-pet-boarding', name: '寄养', kind: 'expense', level: 2, parentId: 'cat-pet', system: false },
  { id: 'cat-insurance', name: '保险', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-insurance-auto', name: '车险', kind: 'expense', level: 2, parentId: 'cat-insurance', system: false },
  { id: 'cat-insurance-medical', name: '医疗险', kind: 'expense', level: 2, parentId: 'cat-insurance', system: false },
  { id: 'cat-insurance-critical-illness', name: '重疾险', kind: 'expense', level: 2, parentId: 'cat-insurance', system: false },
  { id: 'cat-insurance-accident', name: '意外险', kind: 'expense', level: 2, parentId: 'cat-insurance', system: false },
  { id: 'cat-insurance-property', name: '财产险', kind: 'expense', level: 2, parentId: 'cat-insurance', system: false },
  { id: 'cat-insurance-other', name: '其他保费', kind: 'expense', level: 2, parentId: 'cat-insurance', system: false },
  { id: 'cat-invest', name: '投资理财', kind: 'expense', level: 1, parentId: null, system: true },
  { id: 'cat-invest-fund', name: '基金买入', kind: 'expense', level: 2, parentId: 'cat-invest', system: false },
  { id: 'cat-invest-stock', name: '股票买入', kind: 'expense', level: 2, parentId: 'cat-invest', system: false },
  { id: 'cat-invest-wealth', name: '存入理财', kind: 'expense', level: 2, parentId: 'cat-invest', system: false },
  { id: 'cat-invest-gold', name: '黄金买入', kind: 'expense', level: 2, parentId: 'cat-invest', system: false },
  { id: 'cat-invest-bond', name: '债券买入', kind: 'expense', level: 2, parentId: 'cat-invest', system: false },
  { id: 'cat-loan', name: '贷款还款', kind: 'expense', level: 1, parentId: null, system: true },
  { id: 'cat-loan-principal', name: '贷款本金还款', kind: 'expense', level: 2, parentId: 'cat-loan', system: false },
  { id: 'cat-loan-interest', name: '利息支出', kind: 'expense', level: 2, parentId: 'cat-loan', system: false },
  { id: 'cat-loan-installment', name: '信用卡分期', kind: 'expense', level: 2, parentId: 'cat-loan', system: false },
  { id: 'cat-loan-payable-principal', name: '应付本金支付', kind: 'expense', level: 2, parentId: 'cat-loan', system: false },
  { id: 'cat-other', name: '其他', kind: 'expense', level: 1, parentId: null, system: true },
  { id: 'cat-other-misc', name: '其他支出', kind: 'expense', level: 2, parentId: 'cat-other', system: false },
  { id: 'cat-other-fee', name: '手续费', kind: 'expense', level: 2, parentId: 'cat-other', system: false },
  { id: 'cat-other-penalty', name: '罚款赔付', kind: 'expense', level: 2, parentId: 'cat-other', system: false },
  { id: 'cat-other-loss', name: '意外损失', kind: 'expense', level: 2, parentId: 'cat-other', system: false },
  // 🔴 2026-10-13 `O2-C1` 补齐 —— 规格 §2「分类追加块」支出 + L2「报销 / 理赔冲减」
  //    （`DR-016` 之十三 → `DR-022` Q4 泛化命名：公司报销与保险理赔共用同一分类）。
  //    ⚠️ 归属父级 = `cat-other`（系统 L1）：规格 §2 只点名了这条 L2、未指定父级，
  //    按「引擎/口径类细目下沉到 system L1」的既有先例（贷款本金还款 → `cat-loan`）落此。
  { id: 'cat-other-offset', name: '报销/理赔冲减', kind: 'expense', level: 2, parentId: 'cat-other', system: false },
  // 🔴 2026-10-13 `O2-C1` 补齐 —— 规格 §2「分类追加块」支出 + L1「税费」「社保缴费」
  { id: 'cat-tax', name: '税费', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-social-insurance', name: '社保缴费', kind: 'expense', level: 1, parentId: null, system: false },
  { id: 'cat-salary', name: '工资', kind: 'income', level: 1, parentId: null, system: false },
  { id: 'cat-salary-wage', name: '工资', kind: 'income', level: 2, parentId: 'cat-salary', system: false },
  { id: 'cat-salary-bonus', name: '年终奖', kind: 'income', level: 2, parentId: 'cat-salary', system: false },
  { id: 'cat-salary-allowance', name: '奖金津贴', kind: 'income', level: 2, parentId: 'cat-salary', system: false },
  { id: 'cat-salary-subsidy', name: '补助', kind: 'income', level: 2, parentId: 'cat-salary', system: false },
  { id: 'cat-sidehustle', name: '兼职外快', kind: 'income', level: 1, parentId: null, system: false },
  { id: 'cat-sidehustle-parttime', name: '兼职', kind: 'income', level: 2, parentId: 'cat-sidehustle', system: false },
  { id: 'cat-sidehustle-royalty', name: '稿费创作', kind: 'income', level: 2, parentId: 'cat-sidehustle', system: false },
  { id: 'cat-sidehustle-resale', name: '闲置出售', kind: 'income', level: 2, parentId: 'cat-sidehustle', system: false },
  { id: 'cat-sidehustle-rebate', name: '平台返利', kind: 'income', level: 2, parentId: 'cat-sidehustle', system: false },
  { id: 'cat-rent-income', name: '租金收入', kind: 'income', level: 1, parentId: null, system: false },
  { id: 'cat-rent-income-rent', name: '房租收入', kind: 'income', level: 2, parentId: 'cat-rent-income', system: false },
  { id: 'cat-rent-income-deposit-refund', name: '押金退还', kind: 'income', level: 2, parentId: 'cat-rent-income', system: false },
  { id: 'cat-redpacket', name: '红包礼金', kind: 'income', level: 1, parentId: null, system: false },
  { id: 'cat-redpacket-wechat', name: '微信红包', kind: 'income', level: 2, parentId: 'cat-redpacket', system: false },
  { id: 'cat-redpacket-lucky-money', name: '压岁钱', kind: 'income', level: 2, parentId: 'cat-redpacket', system: false },
  { id: 'cat-redpacket-elder-gift', name: '长辈给予', kind: 'income', level: 2, parentId: 'cat-redpacket', system: false },
  { id: 'cat-reimburse', name: '报销', kind: 'income', level: 1, parentId: null, system: false },
  { id: 'cat-reimburse-travel', name: '差旅报销', kind: 'income', level: 2, parentId: 'cat-reimburse', system: false },
  { id: 'cat-reimburse-medical', name: '医保报销', kind: 'income', level: 2, parentId: 'cat-reimburse', system: false },
  { id: 'cat-reimburse-invoice', name: '发票报销', kind: 'income', level: 2, parentId: 'cat-reimburse', system: false },
  // 🔴 2026-10-13 `O2-C1` 补齐 —— 规格 §2「分类追加块」收入 + L1「理赔收入」
  //    （`DR-022` Q8 冲减上限规则：超出原支出的部分一律记收入 ⇒ 保险理赔走这里）
  { id: 'cat-claim-income', name: '理赔收入', kind: 'income', level: 1, parentId: null, system: false },
  { id: 'cat-receivable', name: '应收收回', kind: 'income', level: 1, parentId: null, system: true },
  { id: 'cat-receivable-advance-back', name: '垫付收回', kind: 'income', level: 2, parentId: 'cat-receivable', system: false },
  { id: 'cat-receivable-loan-back', name: '借出收回', kind: 'income', level: 2, parentId: 'cat-receivable', system: false },
  { id: 'cat-invest-gain', name: '投资收益', kind: 'income', level: 1, parentId: null, system: true },
  { id: 'cat-invest-gain-sell', name: '卖出收益', kind: 'income', level: 2, parentId: 'cat-invest-gain', system: false },
  { id: 'cat-invest-gain-dividend', name: '分红', kind: 'income', level: 2, parentId: 'cat-invest-gain', system: false },
  { id: 'cat-invest-gain-interest', name: '利息', kind: 'income', level: 2, parentId: 'cat-invest-gain', system: false },
  { id: 'cat-invest-gain-fx-gain', name: '汇兑收益', kind: 'income', level: 2, parentId: 'cat-invest-gain', system: false },
  { id: 'cat-invest-gain-fx-loss', name: '汇兑损失', kind: 'income', level: 2, parentId: 'cat-invest-gain', system: false },
  { id: 'cat-other-income', name: '其他收入', kind: 'income', level: 1, parentId: null, system: true },
  { id: 'cat-other-income-lottery', name: '中奖', kind: 'income', level: 2, parentId: 'cat-other-income', system: false },
  { id: 'cat-other-income-found', name: '拾获', kind: 'income', level: 2, parentId: 'cat-other-income', system: false },
  { id: 'cat-other-income-misc', name: '其他收入', kind: 'income', level: 2, parentId: 'cat-other-income', system: false },
];

/**
 * 分类 `id` 常量 —— 🔴 **代码引用分类的唯一合法形式**（规格 §22.4 禁按中文名引用）。
 *
 * 派生规则：`id` 去掉 `cat-` 前缀 → 连字符转下划线 → 全大写
 * （`cat-meal-breakfast` → `CAT.MEAL_BREAKFAST`）。**名字由 `id` 决定、不由人决定** ——
 * 这样改中文名不会让常量名跟着漂。
 */
export const CAT = {
  MEAL: 'cat-meal',
  MEAL_BREAKFAST: 'cat-meal-breakfast',
  MEAL_LUNCH: 'cat-meal-lunch',
  MEAL_DINNER: 'cat-meal-dinner',
  MEAL_LATENIGHT: 'cat-meal-latenight',
  MEAL_TAKEOUT: 'cat-meal-takeout',
  MEAL_FASTFOOD: 'cat-meal-fastfood',
  MEAL_COFFEE: 'cat-meal-coffee',
  MEAL_SNACK: 'cat-meal-snack',
  MEAL_DINING: 'cat-meal-dining',
  MEAL_TOBACCO_ALCOHOL: 'cat-meal-tobacco-alcohol',
  GROCERY: 'cat-grocery',
  GROCERY_VEGGIE_FRUIT: 'cat-grocery-veggie-fruit',
  GROCERY_MEAT_DAIRY: 'cat-grocery-meat-dairy',
  GROCERY_SEAFOOD: 'cat-grocery-seafood',
  GROCERY_STAPLE_SEASONING: 'cat-grocery-staple-seasoning',
  GROCERY_BAKERY: 'cat-grocery-bakery',
  GROCERY_FRESH_DELIVERY: 'cat-grocery-fresh-delivery',
  GROCERY_CONVENIENCE: 'cat-grocery-convenience',
  TRANSIT: 'cat-transit',
  TRANSIT_BUS_METRO: 'cat-transit-bus-metro',
  TRANSIT_TAXI: 'cat-transit-taxi',
  TRANSIT_BIKE: 'cat-transit-bike',
  TRANSIT_FUEL: 'cat-transit-fuel',
  TRANSIT_PARKING_TOLL: 'cat-transit-parking-toll',
  TRANSIT_FLIGHT_TRAIN: 'cat-transit-flight-train',
  TRANSIT_CAR_MAINTENANCE: 'cat-transit-car-maintenance',
  TRANSIT_CAR_RENTAL: 'cat-transit-car-rental',
  SHOPPING: 'cat-shopping',
  SHOPPING_DAILY: 'cat-shopping-daily',
  SHOPPING_HOME_TEXTILE: 'cat-shopping-home-textile',
  SHOPPING_KITCHENWARE: 'cat-shopping-kitchenware',
  SHOPPING_BOOK: 'cat-shopping-book',
  SHOPPING_SECONDHAND: 'cat-shopping-secondhand',
  SHOPPING_ONLINE_OTHER: 'cat-shopping-online-other',
  HOUSING: 'cat-housing',
  HOUSING_RENT: 'cat-housing-rent',
  HOUSING_PROPERTY_FEE: 'cat-housing-property-fee',
  HOUSING_REPAIR: 'cat-housing-repair',
  HOUSING_RENOVATION: 'cat-housing-renovation',
  HOUSING_CLEANING: 'cat-housing-cleaning',
  UTILITY: 'cat-utility',
  UTILITY_WATER: 'cat-utility-water',
  UTILITY_ELECTRICITY: 'cat-utility-electricity',
  UTILITY_GAS: 'cat-utility-gas',
  UTILITY_HEATING: 'cat-utility-heating',
  UTILITY_WASTE: 'cat-utility-waste',
  MEDICAL: 'cat-medical',
  MEDICAL_CLINIC: 'cat-medical-clinic',
  MEDICAL_PHARMACY: 'cat-medical-pharmacy',
  MEDICAL_CHECKUP_VACCINE: 'cat-medical-checkup-vaccine',
  MEDICAL_DENTAL_EYE: 'cat-medical-dental-eye',
  MEDICAL_INPATIENT: 'cat-medical-inpatient',
  MEDICAL_SUPPLEMENT: 'cat-medical-supplement',
  MEDICAL_THERAPY: 'cat-medical-therapy',
  EDUCATION: 'cat-education',
  EDUCATION_TUITION: 'cat-education-tuition',
  EDUCATION_TUTORING: 'cat-education-tutoring',
  EDUCATION_HOBBY_CLASS: 'cat-education-hobby-class',
  EDUCATION_COURSE: 'cat-education-course',
  EDUCATION_VOCATIONAL: 'cat-education-vocational',
  EDUCATION_EXAM_CERT: 'cat-education-exam-cert',
  SOCIAL: 'cat-social',
  SOCIAL_REDPACKET: 'cat-social-redpacket',
  SOCIAL_GIFT_MONEY: 'cat-social-gift-money',
  SOCIAL_GIFT: 'cat-social-gift',
  SOCIAL_ELDER_CARE: 'cat-social-elder-care',
  SOCIAL_GATHERING: 'cat-social-gathering',
  SOCIAL_CHARITY: 'cat-social-charity',
  FUN: 'cat-fun',
  FUN_MOVIE: 'cat-fun-movie',
  FUN_SUBSCRIPTION: 'cat-fun-subscription',
  FUN_GAME: 'cat-fun-game',
  FUN_FITNESS: 'cat-fun-fitness',
  FUN_SIGHTSEEING: 'cat-fun-sightseeing',
  FUN_TRAVEL: 'cat-fun-travel',
  FUN_BOARDGAME: 'cat-fun-boardgame',
  BABY: 'cat-baby',
  BABY_FORMULA_DIAPER: 'cat-baby-formula-diaper',
  BABY_TOY_BOOK: 'cat-baby-toy-book',
  BABY_PRESCHOOL: 'cat-baby-preschool',
  BABY_KID_CLOTHING: 'cat-baby-kid-clothing',
  BABY_KID_PLAY: 'cat-baby-kid-play',
  PET: 'cat-pet',
  PET_FOOD: 'cat-pet-food',
  PET_SUPPLIES: 'cat-pet-supplies',
  PET_VET: 'cat-pet-vet',
  PET_GROOMING: 'cat-pet-grooming',
  PET_BOARDING: 'cat-pet-boarding',
  INSURANCE: 'cat-insurance',
  INSURANCE_AUTO: 'cat-insurance-auto',
  INSURANCE_MEDICAL: 'cat-insurance-medical',
  INSURANCE_CRITICAL_ILLNESS: 'cat-insurance-critical-illness',
  INSURANCE_ACCIDENT: 'cat-insurance-accident',
  INSURANCE_PROPERTY: 'cat-insurance-property',
  INSURANCE_OTHER: 'cat-insurance-other',
  INVEST: 'cat-invest',
  INVEST_FUND: 'cat-invest-fund',
  INVEST_STOCK: 'cat-invest-stock',
  INVEST_WEALTH: 'cat-invest-wealth',
  INVEST_GOLD: 'cat-invest-gold',
  INVEST_BOND: 'cat-invest-bond',
  LOAN: 'cat-loan',
  LOAN_PRINCIPAL: 'cat-loan-principal',
  LOAN_INTEREST: 'cat-loan-interest',
  LOAN_INSTALLMENT: 'cat-loan-installment',
  LOAN_PAYABLE_PRINCIPAL: 'cat-loan-payable-principal',
  OTHER: 'cat-other',
  OTHER_MISC: 'cat-other-misc',
  OTHER_FEE: 'cat-other-fee',
  OTHER_PENALTY: 'cat-other-penalty',
  OTHER_LOSS: 'cat-other-loss',
  SALARY: 'cat-salary',
  SALARY_WAGE: 'cat-salary-wage',
  SALARY_BONUS: 'cat-salary-bonus',
  SALARY_ALLOWANCE: 'cat-salary-allowance',
  SALARY_SUBSIDY: 'cat-salary-subsidy',
  SIDEHUSTLE: 'cat-sidehustle',
  SIDEHUSTLE_PARTTIME: 'cat-sidehustle-parttime',
  SIDEHUSTLE_ROYALTY: 'cat-sidehustle-royalty',
  SIDEHUSTLE_RESALE: 'cat-sidehustle-resale',
  SIDEHUSTLE_REBATE: 'cat-sidehustle-rebate',
  RENT_INCOME: 'cat-rent-income',
  RENT_INCOME_RENT: 'cat-rent-income-rent',
  RENT_INCOME_DEPOSIT_REFUND: 'cat-rent-income-deposit-refund',
  REDPACKET: 'cat-redpacket',
  REDPACKET_WECHAT: 'cat-redpacket-wechat',
  REDPACKET_LUCKY_MONEY: 'cat-redpacket-lucky-money',
  REDPACKET_ELDER_GIFT: 'cat-redpacket-elder-gift',
  REIMBURSE: 'cat-reimburse',
  REIMBURSE_TRAVEL: 'cat-reimburse-travel',
  REIMBURSE_MEDICAL: 'cat-reimburse-medical',
  REIMBURSE_INVOICE: 'cat-reimburse-invoice',
  RECEIVABLE: 'cat-receivable',
  RECEIVABLE_ADVANCE_BACK: 'cat-receivable-advance-back',
  RECEIVABLE_LOAN_BACK: 'cat-receivable-loan-back',
  INVEST_GAIN: 'cat-invest-gain',
  INVEST_GAIN_SELL: 'cat-invest-gain-sell',
  INVEST_GAIN_DIVIDEND: 'cat-invest-gain-dividend',
  INVEST_GAIN_INTEREST: 'cat-invest-gain-interest',
  INVEST_GAIN_FX_GAIN: 'cat-invest-gain-fx-gain',
  INVEST_GAIN_FX_LOSS: 'cat-invest-gain-fx-loss',
  OTHER_INCOME: 'cat-other-income',
  OTHER_INCOME_LOTTERY: 'cat-other-income-lottery',
  OTHER_INCOME_FOUND: 'cat-other-income-found',
  OTHER_INCOME_MISC: 'cat-other-income-misc',
  // 🔴 2026-10-13 `O2-C1` 补齐 14 项（规格 §2「分类追加块」+「数码家电/服饰美容→购物、通讯→生活缴费」）
  TAX: 'cat-tax',
  SOCIAL_INSURANCE: 'cat-social-insurance',
  UTILITY_PHONE: 'cat-utility-phone',
  UTILITY_BROADBAND: 'cat-utility-broadband',
  UTILITY_EXPRESS: 'cat-utility-express',
  SHOPPING_CLOTHING: 'cat-shopping-clothing',
  SHOPPING_APPAREL: 'cat-shopping-apparel',
  SHOPPING_BAGS_SHOES: 'cat-shopping-bags-shoes',
  SHOPPING_ACCESSORIES: 'cat-shopping-accessories',
  SHOPPING_HAIR_BEAUTY: 'cat-shopping-hair-beauty',
  SHOPPING_SKINCARE: 'cat-shopping-skincare',
  SHOPPING_DIGITAL: 'cat-shopping-digital',
  OTHER_OFFSET: 'cat-other-offset',
  CLAIM_INCOME: 'cat-claim-income',
} as const;

export type CategoryId = (typeof CAT)[keyof typeof CAT];

// ═══════════ 常用分类集合（B 域 PRD §5.4.4 拍板口径 · 🔴 单一源） ═══════════

/**
 * 「常用分类」默认集合 —— 🔴 **B4 拍板口径的唯一实现处**（B 域 PRD **§5.4.4** · 2026-09-22 拍板）。
 *
 * ## 它是什么 / 不是什么
 *
 * - ✅ 是**展示子集**的选取结果：从 L1 里挑 **10 个**做首屏默认露出（`AC-B4.1` / `AC-B4.4`）；
 * - ❌ **不是"分类清单"** —— 分类清单的唯一源是上面的 `CATEGORIES`；本集合只是它的**引用**，
 *   因此不会成为"第二份真相"（改中文名 / 改图标都不影响这里，因为引用的是 `id`）。
 *
 * ## 口径（B 域 PRD §5.4.4 原文）
 *
 * - 选取范围**锁定 16 支出 + 8 收入 L1**（`AC-B4.5`，不扩大候选）；
 * - 默认 **10 = 9 个支出 L1 + 收入侧的「工资」**；方向切换时**各取各的**（消费方按 `kind` 过滤）；
 * - 原文定性：「**通用家庭场景为主 + 负责人数据微调**」；用「教育」**替换**「娱乐休闲」
 *   —— 理由是家盈定位"一个账本全家共用"，教育是家庭刚需高频，娱乐休闲折叠至第 2 级（1 次可达）；
 * - 🔴 **`system` 分类不进常用集** —— 它们是「安排 / 引擎」入口而非日常记账入口
 *   （原文点名 `cat-loan`：`DR-005` 定本金不计支出，它是「还款安排」的入口；其余 system 同理）。
 *
 * ## 🔴 为什么放在共享层（而不是服务端常量 / 端侧硬编）
 *
 * `DR-021` §6 + B 域 PRD **§5.3.5「跨端单源」**：Web 与小程序必须看到**同一份**常用集，
 * 且它要能被**测试期**校验（见 `categories.spec.ts` 的「常用集」段）。
 * ⇒ 服务端 `GET /api/categories` 读本集合算出 `common` 标记下发，
 * **端侧对"哪些是常用"零知识**（端侧只渲染 `common === true` 的行）。
 *
 * 📌 所以 `AC-B4.8`（用户可自定义常用集合）将来落地时，**只在服务端换数据源**
 * （读 `user_preferences`）即可，Web 与小程序**一行都不用改** —— 这正是把标记放在响应里
 * 而不是让端侧自己筛的理由。
 *
 * ⚠️ **数组顺序 = 展示顺序**（B 域 PRD 未规定顺序 ⇒ 取「通用家庭高频」直觉序，
 * 与设计原型 `03-记一笔_原型.html` 的既有段序保持一致）。
 */
export const COMMON_L1_IDS: readonly CategoryId[] = [
  CAT.MEAL, // 餐饮
  CAT.GROCERY, // 食品生鲜
  CAT.TRANSIT, // 交通
  CAT.SHOPPING, // 购物
  CAT.HOUSING, // 住房
  CAT.UTILITY, // 生活缴费
  CAT.MEDICAL, // 医疗健康
  CAT.EDUCATION, // 教育
  CAT.SOCIAL, // 人情社交
  CAT.SALARY, // 工资（收入侧）
];
